import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Input component with automatic uppercase transformation
 * 
 * Features:
 * - Transforms input to UPPERCASE and stores the value in uppercase
 * - Preserves cursor position while typing (no cursor jump)
 * - Auto-excludes: password, email, number, date, file, hidden, search, tel, url
 * - Manual opt-out: Set uppercase={false} to disable
 * - Number inputs: Converted to text with inputMode="decimal" to prevent scroll issues
 *   while maintaining mobile numeric keyboard support
 * 
 * Usage:
 *   <Input />                    // Uppercase enabled (default)
 *   <Input uppercase={false} />  // Uppercase disabled
 *   <Input type="password" />    // Auto-excluded, no uppercase
 *   <Input type="number" />      // Converted to text input with numeric filtering
 */
const Input = React.forwardRef(({ className, type, uppercase = true, onChange, style, ...props }, ref) => {
  // Check if this is a number input - we'll convert it to text with numeric filtering
  const isNumberInput = type === 'number';
  
  // Determine if uppercase should be applied based on type and prop
  const excludedTypes = ['password', 'email', 'number', 'date', 'file', 'hidden', 'search', 'tel', 'url'];
  const shouldUppercase = uppercase && !excludedTypes.includes(type);

  // Handle input change with cursor-preserving uppercase transformation
  const handleChange = (e) => {
    // For number inputs, filter to only allow valid numeric characters
    if (isNumberInput) {
      const value = e.target.value;
      // Allow empty string (clearing), minus sign alone (typing negative), or valid decimal numbers
      if (value === '' || value === '-' || /^-?\d*\.?\d*$/.test(value)) {
        onChange?.(e);
      }
      // Invalid characters are silently rejected - nothing happens
      return;
    }
    
    if (shouldUppercase) {
      const input = e.target;
      const cursorStart = input.selectionStart;
      const cursorEnd = input.selectionEnd;
      
      // Transform to uppercase
      const upperValue = input.value.toUpperCase();
      
      // Only update if value changed (prevents unnecessary cursor manipulation)
      if (input.value !== upperValue) {
        input.value = upperValue;
        // Restore cursor position after transformation
        input.setSelectionRange(cursorStart, cursorEnd);
      }
    }
    
    // Call the original onChange handler
    onChange?.(e);
  };

  // Determine the actual input type and additional props for number inputs
  const inputType = isNumberInput ? 'text' : type;
  const numberProps = isNumberInput ? {
    inputMode: 'decimal',
    pattern: '[0-9]*\\.?[0-9]*'
  } : {};

  return (
    <input
      type={inputType}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      onChange={handleChange}
      style={style}
      {...numberProps}
      {...props} />
  );
})
Input.displayName = "Input"

export { Input }
